/* eslint-disable react-refresh/only-export-components */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { registerTokenGetter } from '@/features/auth/tokenProvider';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Register token getter using Auth0's getAccessTokenSilently
function TokenBridge() {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  // Register once on mount
  registerTokenGetter(async () => {
    if (!isAuthenticated) {
      return null;
    }
    try {
      const audience = import.meta.env['VITE_AUTH0_AUDIENCE'] as string | undefined;
      const scope =
        (import.meta.env['VITE_AUTH0_SCOPE'] as string | undefined) ??
        'openid profile email offline_access';
      const authorizationParams: Record<string, string> = {};
      if (audience) {
        authorizationParams['audience'] = audience;
      }
      if (scope) {
        authorizationParams['scope'] = scope;
      }

      const token = await getAccessTokenSilently(
        Object.keys(authorizationParams).length > 0 ? { authorizationParams } : undefined
      );
      return token;
    } catch {
      await loginWithRedirect();
      return null;
    }
  });
  return <App />;
}

const cacheLocation =
  (import.meta.env['VITE_AUTH0_CACHE'] as 'memory' | 'localstorage' | undefined) ?? 'memory';

createRoot(rootElement).render(
  <StrictMode>
    <Auth0Provider
      domain={import.meta.env['VITE_AUTH0_DOMAIN'] as string}
      clientId={import.meta.env['VITE_AUTH0_CLIENT_ID'] as string}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: (import.meta.env['VITE_AUTH0_AUDIENCE'] as string) ?? '',
        scope:
          (import.meta.env['VITE_AUTH0_SCOPE'] as string) ?? 'openid profile email offline_access',
      }}
      cacheLocation={cacheLocation}
      useRefreshTokens
      onRedirectCallback={(appState?: unknown) => {
        const state = appState as { returnTo?: string } | undefined;
        const targetUrl = state?.returnTo || window.location.pathname;
        window.history.replaceState({}, document.title, targetUrl);
      }}
    >
      <TokenBridge />
    </Auth0Provider>
  </StrictMode>
);

export {};
